import { useNavigate } from 'react-router-dom'
import { useHomeData, useTodayCurve, useYesterday } from '../lib/home'
import { clock, elapsedText, money, moneyShort, qtyToText } from '../lib/money'
import { isOwner, useApp } from '../lib/store'
import { Sparkline } from '../components/Sparkline'

/**
 * Màn hình chính, bộ Modern.
 *
 * Đọc cùng một nguồn số liệu với bộ Classic, chỉ khác cách bày. Thứ tự trên xuống là thứ
 * tự chủ quán hỏi khi mở app: hôm nay được bao nhiêu, két đang phải có bao nhiêu, giờ
 * bấm vào đâu để bán.
 */

/** Câu chào đổi theo giờ. Quán cà phê mở lúc 6 giờ sáng và đóng lúc 10 giờ đêm. */
function greeting(hour: number): { icon: string; text: string } {
  if (hour < 11) return { icon: '☀️', text: 'Chào buổi sáng!' }
  if (hour < 14) return { icon: '🍚', text: 'Chào buổi trưa!' }
  if (hour < 18) return { icon: '🌤️', text: 'Chào buổi chiều!' }
  return { icon: '🌙', text: 'Chào buổi tối!' }
}

interface StatProps {
  label: string
  value: number
  tone: 'brand' | 'clay' | 'violet'
  icon: string
  curve: number[]
  /** Số hôm qua để so. `null` là máy này chưa có số hôm qua. */
  yesterday: number | null
}

function Stat({ label, value, tone, icon, curve, yesterday }: StatProps) {
  // Hôm qua bằng 0 mà hôm nay có tiền thì phần trăm là vô cực — nói "mới có hôm nay"
  // thay vì in một con số vô nghĩa cạnh doanh thu.
  const delta =
    yesterday === null ? null : yesterday === 0 ? null : Math.round(((value - yesterday) / yesterday) * 100)

  return (
    <div className={`stat is-${tone}`}>
      <Sparkline points={curve} tone={tone} />

      <div className="stat-head">
        <span className="stat-icon" aria-hidden="true">
          {icon}
        </span>
        <span className="stat-key">{label}</span>
      </div>

      <b className="stat-val num">{money(value)}</b>

      <div className="stat-foot">
        {delta === null ? (
          <span className="stat-flat">
            {yesterday === null ? 'Chưa có số hôm qua' : 'Hôm qua chưa có khoản này'}
          </span>
        ) : (
          <>
            <span>So với hôm qua</span>
            <span className={`stat-delta${delta < 0 ? ' is-down' : ''}`}>
              <span aria-hidden="true">{delta < 0 ? '↓' : '↑'}</span> {Math.abs(delta)}%
            </span>
          </>
        )}
      </div>
    </div>
  )
}

interface ActionProps {
  tone: string
  icon: string
  title: string
  sub: string
  onClick: () => void
}

function Action({ tone, icon, title, sub, onClick }: ActionProps) {
  return (
    <button type="button" className={`action is-${tone}`} onClick={onClick}>
      <span className="action-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="action-text">
        <b>{title}</b>
        <small>{sub}</small>
      </span>
      <span className="action-go" aria-hidden="true">
        →
      </span>
    </button>
  )
}

export function HomeModernScreen() {
  const nav = useNavigate()
  const shop = useApp((s) => s.shop)
  const owner = isOwner(useApp((s) => s.session?.role))

  const { now, lastPullAt, totals, shift, shiftTally, top, busy, spendCount } = useHomeData()
  const yesterday = useYesterday()
  const curve = useTodayCurve()

  const hello = greeting(new Date(now).getHours())

  return (
    <div className="screen">
      <div className="body board">
        <section className="hero">
          <div className="hero-text">
            <p className="hero-hi">
              <span aria-hidden="true">{hello.icon}</span> {hello.text}
            </p>
            <h1>{shop?.name ?? 'Quán của tôi'}</h1>
            <p className="hero-sub">
              {busy > 0
                ? `Đang có ${busy} bàn khách ngồi. Bấm Bán tại bàn để ghi món.`
                : 'Cùng quản lý đơn hàng, doanh thu và khách hàng hiệu quả hơn.'}
            </p>
          </div>
          <div className="hero-art" aria-hidden="true">
            <svg viewBox="0 0 220 150" focusable="false">
              <ellipse cx="112" cy="126" rx="78" ry="9" fill="var(--brand)" opacity=".12" />
              <path d="M62 66h84v26a30 30 0 0 1-30 30H92a30 30 0 0 1-30-30z" fill="var(--brand)" opacity=".9" />
              <path d="M146 74h12a17 17 0 0 1 0 34h-6" fill="none" stroke="var(--brand)" strokeWidth="7" opacity=".9" />
              <ellipse cx="104" cy="66" rx="42" ry="9" fill="var(--paper)" opacity=".95" />
              <path d="M104 60c9 0 15 3 15 6s-6 6-15 6-15-3-15-6 6-6 15-6z" fill="var(--brand-soft)" />
              <path d="M86 40c0-11 7-19 18-21-1 12-7 19-18 21z" fill="var(--brand)" opacity=".45" />
              <path d="M122 44c0-9 5-15 14-17-1 10-6 16-14 17z" fill="var(--brand)" opacity=".3" />
              <path d="M104 19v27" stroke="var(--brand)" strokeWidth="2.4" opacity=".45" strokeLinecap="round" />
            </svg>
          </div>
        </section>

        {/*
          * Người bán hàng không thấy ba con số này. Họ vẫn thấy số tiền phải có trong két
          * ở dải ca bên dưới — đó là thứ họ chịu trách nhiệm — nhưng doanh thu và lãi
          * trong ngày là chuyện của chủ quán.
          */}
        {owner && (
          <section className="stats">
            <Stat
              label="Tiền vô"
              value={totals?.revenue ?? 0}
              tone="brand"
              icon="📈"
              curve={curve}
              yesterday={yesterday?.revenue ?? null}
            />
            <Stat
              label="Tiền ra"
              value={totals?.expense ?? 0}
              tone="clay"
              icon="🧺"
              curve={[]}
              yesterday={yesterday?.expense ?? null}
            />
            <Stat
              label="Còn lại"
              value={totals?.profit ?? 0}
              tone="violet"
              icon="🪙"
              curve={curve}
              yesterday={yesterday?.profit ?? null}
            />
          </section>
        )}

        {/* Ca là thứ chủ quán hỏi đầu tiên khi thuê người bán: két đang phải có bao nhiêu. */}
        <button
          type="button"
          className={`shift-bar${shift ? ' is-open' : ''}`}
          onClick={() => nav('/shift')}
        >
          <span className="shift-bar-icon" aria-hidden="true">
            {shift ? '🟢' : '🕐'}
          </span>
          <span className="shift-bar-text">
            <b>{shift ? `Ca mở từ ${clock(shift.openedAt)}` : 'Chưa mở ca'}</b>
            <small>
              {shift
                ? `đã ${elapsedText(shift.openedAt, now)} · két phải có ${money(shiftTally?.expectedCash ?? shift.openingCash)}đ`
                : 'Mở ca để cuối ngày biết két lệch bao nhiêu'}
            </small>
          </span>
          <span className="shift-bar-go">
            {shift ? 'Chốt ca' : '▶ Mở ca'} <span aria-hidden="true">›</span>
          </span>
        </button>

        <section className="actions">
          <Action
            tone="brand"
            icon="🪑"
            title="Bán tại bàn"
            sub={busy > 0 ? `${busy} bàn đang có khách` : 'Tất cả bàn trống'}
            onClick={() => nav('/tables')}
          />
          <Action
            tone="amber"
            icon="⚡"
            title="Bán nhanh"
            sub="Không cần chọn bàn"
            onClick={() => nav('/quick')}
          />
          <Action
            tone="clay"
            icon="🧺"
            title="Nhập hàng"
            sub={spendCount > 0 ? `${spendCount} phiếu hôm nay` : 'Chưa ghi phiếu nào'}
            onClick={() => nav('/expense')}
          />
          <Action
            tone="blue"
            icon="🧾"
            title="Hoá đơn"
            sub={totals ? `${totals.orderCount} hoá đơn hôm nay` : 'Tra lại đơn đã bán'}
            onClick={() => nav('/history')}
          />
          {owner && (
            <Action
              tone="violet"
              icon="📄"
              title="Báo cáo thuế"
              sub="Sổ doanh thu theo quý"
              onClick={() => nav('/report')}
            />
          )}
        </section>

        {owner && top.length > 0 && (
          <section className="card">
            <h2 className="section-label">Bán chạy hôm nay</h2>
            <div className="list">
              {top.map((t) => (
                <div key={t.name} className="list-row">
                  <span className="list-name">{t.name}</span>
                  <span className="list-qty num">{qtyToText(t.qtyMilli)}</span>
                  <span className="list-amount num">{moneyShort(t.revenue)}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="board-foot">
          <svg viewBox="0 0 160 16" className="board-squiggle" aria-hidden="true" focusable="false">
            <path
              d="M2 12C30 12 40 3 80 3s50 9 78 9"
              fill="none"
              stroke="var(--brand)"
              strokeWidth="1.6"
              strokeLinecap="round"
              opacity=".35"
            />
          </svg>
          <p className="foot-note">
            {owner && lastPullAt
              ? `Số liệu cả quán, cập nhật lúc ${clock(lastPullAt)}`
              : 'Bấm là bán. Cuối tháng có sổ nộp thuế.'}
          </p>
        </div>
      </div>
    </div>
  )
}
